<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0" >
			<VerticalLayout>
				<StatsChartTitleWithDescription
					v-if="showDescription"
					titleKey="breaks-gantt.header"
					titleClass="pd-lg-x pd-md-top"
				>
					<template #description>
						<TypographyParagraph>{{ $t('breaks-gantt.helpText.p0') }}</TypographyParagraph>
						<TypographyParagraph>{{ $t('breaks-gantt.helpText.p1') }}</TypographyParagraph>
						<TypographyParagraph>{{ $t('breaks-gantt.helpText.p2') }}</TypographyParagraph>
					</template>
				</StatsChartTitleWithDescription>
				<Spin :spinning="isLoading" class="pd-lg-x">
					<svg class="gantt" ref="ganttTag"></svg>
				</Spin>
			</VerticalLayout>
		</Col>
		<Col :span="6" :offset="0" >
			<VerticalLayout>
				<Card :bordered="false">
					<VerticalLayout>
						<LabeledSwitch v-model="showAllBreaks" :rightLabel="$t('breaks-gantt.showAllBreaksLabel')" @change="renderChart()"/>

						<div>{{ $t('breaks-gantt.mountpointsLabel') }}</div>
						<Select
							class="width-100"
							v-model:value="mountpointsSelected"
							mode="multiple"
							:allowClear="true"
							:options="mountpointsOptions"
							@change="setSubnetworkOnMountpointsChange"
						/>

						<SubNetworkSelect
							ref="subNetworkSelect"
							:onChange="setMountpointsForSubnetwork"
							:allowClear="true"
						/>

						<DateRangeChooser v-show="showDescription" v-model="datesRange" @change="ganttBreaksGet" />
					</VerticalLayout>
				</Card>
				<Card v-if="sessionStore.debug" :bordered="false">
					<PopconfirmButton
						:title="$t('common.dangerousOperationLabel')"
						:ok-text="$t('common.confirmButtonLabel')"
						:cancel-text="$t('common.cancelButtonLabel')"
						:onConfirm="onGenerateTestData"
						:loading="isLoadingTestData"
						isDangerousOperation
					>
						{{ $t('breaks-gantt.generateTestDataButtonLabel') }}
					</PopconfirmButton>
				</Card>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, toRef, watch, computed, onMounted } from 'vue';

import moment from 'moment';

import { useRouter } from 'vue-router';

import { Row, Col, Card, Select, TypographyParagraph, Spin } from 'ant-design-vue';

import Api from '@/api/Api';

import { useI18n } from 'vue-i18n';

import { useSessionStore } from '@/stores/session';
import { useBaseStationsStore } from '@/stores/baseStations';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';
import { useSubNetworkMountpointsFilter } from '@/utils/useSubNetworkMountpointsFilter';

import { renderBreaksGantt } from './breaksGanttPageCore';
import { dayJsToString, warningIfNotUtcMidnight, jsNativeDateToDayJs } from '@/components/common/dateUtils';

import VerticalLayout from '@/components/common/VerticalLayout';
import DateRangeChooser from '@/components/common/DateRangeChooser';
import LabeledSwitch from '@/components/common/LabeledSwitch';
import SubNetworkSelect from '@/components/common/controls/SubNetworkSelect.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';

export default {
	components: {
		Row, Col, Card, Select, TypographyParagraph, Spin,
		VerticalLayout, LabeledSwitch, DateRangeChooser,
		SubNetworkSelect,
		PopconfirmButton,
		StatsChartTitleWithDescription,
	},
	props: {
		showDescription: {
			type: Boolean,
			default: true,
		},
		initMountpointSelected: {
			type: String,
			required: false,
		},
		minDate: {
			type: Date,
			required: false,
		},
		maxDate: {
			type: Date,
			required: false,
		},
	},
	setup(props) {
		const { t } = useI18n();
		const breaks = ref([]);
		const breaksNoData = ref([]);
		const delaysMaxDatetime = ref(null);
		const foundMountpoints = ref([]);
		const { apiData, isLoading, doRequest } = Api.useGanttBreaksGet();
		const showAllBreaks = ref(true);
		const baseStationsStore = useBaseStationsStore();
		const mountpointsOptions = computed(() => baseStationsStore.mountpointsSelectOptions);
		const mountpointsSelected = ref(props.initMountpointSelected ? [props.initMountpointSelected] : []);
		const sessionStore = useSessionStore();
		let datesRange;
		let datesRangeStr;
		if (props.minDate && props.maxDate) {
			warningIfNotUtcMidnight(props.minDate, 'minDate', 'BreaksGanttPage');
			warningIfNotUtcMidnight(props.maxDate, 'maxDate', 'BreaksGanttPage');
			datesRange = ref([jsNativeDateToDayJs(props.minDate), jsNativeDateToDayJs(props.maxDate)]);
			datesRangeStr = ref(datesRange.value.map(dayJsToString));
		}
		else {
			const datesAdapter = useDateRangeAdapter(sessionStore, 1, 0);
			datesRange = datesAdapter.datesRange;
			datesRangeStr = datesAdapter.datesRangeStr;
		}

		const ganttTag = ref();
		const zoomState = ref(null);
		const router = useRouter();
		const messages = computed(() => {
			return {
				turnedOff: t('breaks-gantt.messages.turnedOff'),
				noData: t('breaks-gantt.messages.noData'),
			};
		});
		const renderChart = () => {
			if (datesRange.value == null || ganttTag.value == null) {
				return;
			}
			let mountpoints = mountpointsSelected.value.length > 0 ? mountpointsSelected.value : baseStationsStore.items.map(item => item.mntpnt_int);
			if (mountpoints.length == 0) {
				return;
			}
			mountpoints.sort((a, b) => a.localeCompare(b));
			const mountpointsIdMap = {};
			for (let item of useBaseStationsStore().items) {
				mountpointsIdMap[item.mntpnt_int] = item.id
			}
			const startDate = datesRange.value[0].toDate();
			const endDate = datesRange.value[1].toDate();
			renderBreaksGantt(
				'.gantt',
				breaks.value,
				breaksNoData.value,
				mountpoints,
				foundMountpoints.value,
				mountpointsIdMap,
				showAllBreaks.value,
				ganttTag.value.clientWidth,
				startDate,
				endDate,
				delaysMaxDatetime.value,
				router,
				messages,
				zoomState,
			);
		};

		const {
			subNetworkSelect,
			setMountpointsForSubnetwork,
			setSubnetworkOnMountpointsChange,
		} = useSubNetworkMountpointsFilter(mountpointsSelected, renderChart);

		watch(apiData, responseData => {
			breaks.value = responseData.breaks.map(item => ({
				...item,
				x1: moment(item.start).toDate(),
				x2: moment(item.end).toDate(),
			}));
			breaksNoData.value = responseData.breaksNoData.map(item => ({
				...item,
				x1: moment(item.start).toDate(),
				x2: moment(item.end).toDate(),
			}));
			delaysMaxDatetime.value = responseData.delaysMaxDatetime ? moment(responseData.delaysMaxDatetime).toDate() : null;
			foundMountpoints.value = responseData.foundMountpoints;
			renderChart();
		});

		const ganttBreaksGet = () => {
			if (datesRangeStr.value != null) {
				doRequest({
					start_date: datesRangeStr.value[0],
					end_date: datesRangeStr.value[1],
				});
			}
		};
		onMounted(ganttBreaksGet);
		watch(() => baseStationsStore.items, () => {
			renderChart();
		});

		const initMountpointSelectedRef = toRef(props, 'initMountpointSelected');
		watch(initMountpointSelectedRef, (to, from) => {
			if (from == null && to != null) {
				mountpointsSelected.value = [to];
				ganttBreaksGet();
			}
		});

		const isLoadingTestData = ref(false);
		const onGenerateTestData = () => {
			isLoadingTestData.value = true;
			Api.generateTestBreaks(() => {
				isLoadingTestData.value = false;
				ganttBreaksGet();
			})
		};

		return {
			isLoading,
			showAllBreaks,
			mountpointsOptions,
			mountpointsSelected,
			sessionStore,
			datesRange,
			datesRangeStr,
			subNetworkSelect,
			setMountpointsForSubnetwork,
			setSubnetworkOnMountpointsChange,

			ganttTag,
			renderChart,
			ganttBreaksGet,

			isLoadingTestData,
			onGenerateTestData,
		};
	},
}
</script>

<style>
.gantt {
	width: 100%;
	direction: ltr;
}

.tick text,
.text_tip {
	font-size: 12px;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
}

.y.axis .tick text {
	cursor: pointer;
}

.y.axis .tick text:hover {
	fill: #1890ff;
	text-decoration: underline;
}

.tick-bold {
	font-weight: bold;
}

.empty-text {
	font-size: 14px;
	fill: rgba(0, 0, 0, 0.25);
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
}

.x-axis-legend {
	font-size: 13px;
	fill: black;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
}

.zoom {
	fill: none;
	pointer-events: all;
}

rect.selection {
	stroke: none;
}

div.tooltip {
	position: absolute;
	text-align: center;
	width: 120px;
	height: 40px;
	padding: 2px;
	font: 12px sans-serif;
	background: lightsteelblue;
	border: 0px;
	border-radius: 8px;
	pointer-events: none;
}

.text_tip_bg {
	filter: drop-shadow(0px 2px 3px #444);
}

.measurements {
	opacity: 0.01;
}
</style>
